/**
 * O comando de atalho (`/panorama`, `/pré-reunião Marcelo`) dentro da fala do
 * usuário — para a bolha desenhar o MESMO chip do composer.
 *
 * A fala gravada é `label` ou `label + ' ' + argumento`
 * (`falaDoAtalho`, abaixo, espelho de `core/atalhos/turno.py::_fala_do_comando`),
 * então derivar do texto funciona ao vivo e na conversa reaberta. Casa pelo
 * catálogo carregado; `/qualquer-coisa` fora dele continua texto.
 */
import { normalizeText } from '@utils/Format'

const BARRA = '/'

// O contrato do servidor não manda ícone; `flash` existe em `components/icons/svg/`.
export const ICONE_PADRAO_DO_ATALHO = 'flash'

/**
 * A fala que fica na thread como mensagem do USUÁRIO — **o mesmo texto** que
 * `core/atalhos/turno.py::_fala_do_comando` grava no banco:
 *
 * ```python
 * return f'{atalho.label} {argumento}'.strip() if argumento else atalho.label
 * ```
 *
 * Reproduzido à letra, e não "algo parecido", porque a bolha ao vivo e a
 * bolha depois do F5 são a MESMA mensagem para quem lê: qualquer divergência
 * troca o texto debaixo do usuário no reload. `''`/`null`/`undefined` são
 * falsy nos dois lados (o argumento ausente cai no rótulo puro), e `.trim()`
 * é o par do `.strip()` do Python para argumento só de espaços.
 *
 * O `label` vem do CATÁLOGO do servidor (`GET /v2/api/ia-crm/atalhos` →
 * `Atalho.para_payload()`), viaja pelo menu do `/`
 * (`send-message-atalhos-mixin.js::dispararAtalho`) e chega aqui sem ser
 * recomposto no caminho — é a única forma de ele ser, byte a byte, o mesmo
 * `atalho.label` que o backend usa.
 */
export function falaDoAtalho(label, argumento) {
  if (!argumento) return label
  return (label + ' ' + argumento).trim()
}

export function iconeDoAtalho(atalho) {
  return (atalho && atalho.icone) || ICONE_PADRAO_DO_ATALHO
}

/**
 * O módulo do menu do `/` escrito como comando, igual aos atalhos: `CRM` →
 * `/crm`, `Fee Fixo` → `/fee-fixo` (acento preservado, como `/pré-reunião`).
 * @example rotuloDeComandoDoModulo({ rotulo: 'Financial Planning' }) // '/financial-planning'
 */
export function rotuloDeComandoDoModulo(modulo) {
  const rotulo = String((modulo && modulo.rotulo) || '').trim()
  if (!rotulo) return ''
  return BARRA + rotulo.toLowerCase().replaceAll(/\s+/g, '-')
}

/** O módulo cujo comando (`/crm`) é exatamente o token, sem acento/caixa. */
export function moduloDoComando(token, modulos) {
  if (!token || !Array.isArray(modulos)) return null
  const alvo = normalizeText(token)
  return (
    modulos.find((modulo) => normalizeText(rotuloDeComandoDoModulo(modulo)) === alvo) || null
  )
}

// `/pré-reunião` e o fallback `/pre-reuniao` (`rotuloDoAtalho`) casam o mesmo atalho.
function casaToken(atalho, alvo) {
  if (!atalho) return false
  const pelaLabel = normalizeText(String(atalho.label || '')) === alvo
  const peloId = normalizeText(BARRA + String(atalho.id || '')) === alvo
  return pelaLabel || peloId
}

/**
 * @param {String} texto a fala do usuário
 * @param {Array} atalhos catálogo `{id, label, ...}`
 * @param {Array} [modulos] módulos do menu `{rotulo, icone}` — a fala `/crm …`
 * @returns {{atalho: Object|null, modulo: Object|null, rotulo: String, icone: String, argumento: String}|null}
 * @example separarComandoDaFala('/pré-reunião Ana', catalogo) // {rotulo: '/pré-reunião', argumento: 'Ana', ...}
 */
export function separarComandoDaFala(texto, atalhos, modulos) {
  const fala = String(texto || '').trim()
  if (!fala.startsWith(BARRA)) return null

  const indiceDoEspaco = fala.search(/\s/)
  const token = indiceDoEspaco === -1 ? fala : fala.slice(0, indiceDoEspaco)
  const argumento = indiceDoEspaco === -1 ? '' : fala.slice(indiceDoEspaco).trim()
  const alvo = normalizeText(token)

  const atalho = Array.isArray(atalhos) ? atalhos.find((item) => casaToken(item, alvo)) : null
  if (atalho) {
    return {
      atalho: atalho,
      modulo: null,
      rotulo: atalho.label || token,
      icone: iconeDoAtalho(atalho),
      argumento: argumento,
    }
  }

  const modulo = moduloDoComando(token, modulos)
  if (!modulo) return null
  return {
    atalho: null,
    modulo: modulo,
    rotulo: rotuloDeComandoDoModulo(modulo),
    icone: modulo.icone || ICONE_PADRAO_DO_ATALHO,
    argumento: argumento,
  }
}

// Só atalho do catálogo vira "Usando skill": módulo (`/crm`) não é skill.
function rotuloDoAtalhoNaFala(texto, atalhos) {
  const comando = separarComandoDaFala(texto, atalhos)
  return comando && comando.atalho ? comando.rotulo : null
}

/**
 * O atalho que abriu o turno: a fala do usuário imediatamente anterior à resposta.
 * `null` sem comando, com módulo (`/crm`) ou com `/token` fora do catálogo.
 * @example comandoDoTurno([{ role: 'user', content: '/panorama' }], 1, catalogo) // '/panorama'
 */
export function comandoDoTurno(messages, indiceDaResposta, atalhos) {
  const lista = Array.isArray(messages) ? messages : []
  for (let i = indiceDaResposta - 1; i >= 0; i--) {
    const msg = lista[i]
    if (msg && msg.role === 'user') return rotuloDoAtalhoNaFala(msg.content, atalhos)
  }
  return null
}
